import type {
  TradePolicyComparisonLane,
  TradePolicyMetric,
  TradePolicySimulateResponse,
} from "../../../types/tradePolicy";

export const METRIC_KEYS: { key: keyof TradePolicyMetric; label: string }[] = [
  { key: "trade_count", label: "Trades" },
  { key: "win_count", label: "Wins" },
  { key: "loss_count", label: "Losses" },
  { key: "win_rate", label: "Win rate" },
  { key: "total_pnl", label: "Total PnL" },
  { key: "expectancy", label: "Expectancy" },
  { key: "profit_factor", label: "Profit factor" },
  { key: "max_drawdown", label: "Max drawdown" },
  { key: "max_drawdown_percent", label: "Max DD %" },
];

/** Metrics whose absolute values should use green/red signed tone in results tables. */
export function isSignedPnlMetricKey(key: string): boolean {
  return key === "total_pnl" || key === "expectancy";
}

/** Numeric value for signed coloring, or null when the cell should stay neutral. */
export function signedPnlMetricTone(key: string, val: unknown): number | null {
  if (!isSignedPnlMetricKey(key)) return null;
  if (typeof val !== "number" || !Number.isFinite(val)) return null;
  return val;
}

export function formatMetricValue(key: string, val: unknown): string {
  if (val === null || val === undefined) return "—";
  if (typeof val === "number") {
    if (key === "win_rate") {
      const pct = val >= 0 && val <= 1 ? val * 100 : val;
      return `${pct.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
    }
    if (key === "max_drawdown_percent") {
      return `${val.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
    }
    return val.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  if (typeof val === "boolean") return val ? "Yes" : "No";
  return String(val);
}

export function formatMetricPercentChangeFromBaseline(baselineVal: unknown, filteredVal: unknown): number | null {
  if (typeof baselineVal !== "number" || typeof filteredVal !== "number") return null;
  if (baselineVal === 0) return null;
  return ((filteredVal - baselineVal) / Math.abs(baselineVal)) * 100;
}

export function formatSignedPercentChange(pct: number): string {
  return `${pct >= 0 ? "+" : ""}${pct.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

export function formatSignedNumber(val: number, maximumFractionDigits = 2): string {
  return `${val >= 0 ? "+" : ""}${val.toLocaleString(undefined, { maximumFractionDigits })}`;
}

export function normalizeWinRatePercent(val: unknown): number | null {
  if (typeof val !== "number") return null;
  return val >= 0 && val <= 1 ? val * 100 : val;
}

export function metricDeltaNumber(baselineVal: unknown, filteredVal: unknown): number | null {
  if (typeof baselineVal !== "number" || typeof filteredVal !== "number") return null;
  return filteredVal - baselineVal;
}

function subtractMetricNumber(baselineVal: unknown, filteredVal: unknown): number | undefined {
  if (typeof baselineVal !== "number" || typeof filteredVal !== "number") return undefined;
  return baselineVal - filteredVal;
}

/**
 * Metrics for trades excluded by a filter/veto policy (baseline − filtered).
 * Fallback when BE does not yet return `removed` / `changed` + `meta.comparison_lane`.
 */
export function deriveRemovedMetrics(
  baseline: TradePolicyMetric | undefined,
  filtered: TradePolicyMetric | undefined,
): TradePolicyMetric | undefined {
  if (!baseline || !filtered) return undefined;

  const tradeCount = subtractMetricNumber(baseline.trade_count, filtered.trade_count);
  if (tradeCount == null) return undefined;

  if (tradeCount <= 0) {
    return {
      trade_count: 0,
      win_count: 0,
      loss_count: 0,
      win_rate: undefined,
      total_pnl: undefined,
      expectancy: undefined,
      profit_factor: undefined,
      max_drawdown: undefined,
      max_drawdown_percent: undefined,
    };
  }

  const winCountRaw = subtractMetricNumber(baseline.win_count, filtered.win_count);
  const lossCountRaw = subtractMetricNumber(baseline.loss_count, filtered.loss_count);
  const totalPnl = subtractMetricNumber(baseline.total_pnl, filtered.total_pnl);

  const winCount = winCountRaw == null ? undefined : Math.max(0, winCountRaw);
  const lossCount = lossCountRaw == null ? undefined : Math.max(0, lossCountRaw);
  const decided =
    winCount != null && lossCount != null ? winCount + lossCount : undefined;
  const winRate =
    decided != null && decided > 0 && winCount != null ? (winCount / decided) * 100 : undefined;
  const expectancy =
    totalPnl != null && tradeCount > 0 ? totalPnl / tradeCount : undefined;

  return {
    trade_count: tradeCount,
    win_count: winCount,
    loss_count: lossCount,
    win_rate: winRate,
    total_pnl: totalPnl,
    expectancy,
    profit_factor: undefined,
    max_drawdown: undefined,
    max_drawdown_percent: undefined,
  };
}

export type ComparisonLaneColumn = {
  /** Table cohort column — only `changed` is shown (Removed folds into Final book vs Baseline). */
  lane: "changed";
  header: string;
  metrics: TradePolicyMetric | undefined;
};

function normalizeComparisonLane(
  result:
    | Pick<TradePolicySimulateResponse, "meta" | "removed" | "changed" | "baseline" | "filtered">
    | null
    | undefined
): TradePolicyComparisonLane {
  const laneRaw = result?.meta?.comparison_lane;
  if (laneRaw === "both" || laneRaw === "changed" || laneRaw === "removed") return laneRaw;
  if (result?.removed != null && result?.changed != null) return "both";
  if (result?.changed != null) return "changed";
  return "removed";
}

export function resolveComparisonLane(
  result:
    | Pick<TradePolicySimulateResponse, "meta" | "removed" | "changed" | "baseline" | "filtered">
    | null
    | undefined
): TradePolicyComparisonLane {
  return normalizeComparisonLane(result);
}

/**
 * Header for the `filtered` metrics column (final book after policy).
 * Entry → Filtered; Exit Placement → Simulated; hybrid → Final book.
 */
export function resolveSimulatedBookColumnHeader(
  result:
    | Pick<TradePolicySimulateResponse, "meta" | "removed" | "changed" | "baseline" | "filtered">
    | null
    | undefined
): string {
  const lane = normalizeComparisonLane(result);
  if (lane === "changed") return "Simulated";
  if (lane === "both") return "Final book";
  return "Filtered";
}

/** Delta column: “{middle book} vs Baseline” (pairs with {@link resolveSimulatedBookColumnHeader}). */
export function resolveFinalBookVsBaselineHeader(
  result:
    | Pick<TradePolicySimulateResponse, "meta" | "removed" | "changed" | "baseline" | "filtered">
    | null
    | undefined
): string {
  return `${resolveSimulatedBookColumnHeader(result)} vs Baseline`;
}

/** @deprecated Use {@link resolveFinalBookVsBaselineHeader} — label is lane-dependent. */
export const FINAL_BOOK_VS_BASELINE_HEADER = "Final book vs Baseline";

/**
 * Table cohort columns after Baseline / Simulated book.
 * Shows Changed when EP (or hybrid) is present. Removed is not a table column —
 * use {@link resolveRemovedMetricsForSubtitle} for the summary “−N removed” line.
 */
export function resolveComparisonLaneColumns(
  result:
    | Pick<TradePolicySimulateResponse, "meta" | "removed" | "changed" | "baseline" | "filtered">
    | null
    | undefined
): ComparisonLaneColumn[] {
  const lane = normalizeComparisonLane(result);
  if (lane !== "changed" && lane !== "both") return [];
  return [
    {
      lane: "changed",
      header: "Changed",
      metrics: result?.changed,
    },
  ];
}

/** Removed cohort metrics for summary subtitle only (not a metrics-table column). */
export function resolveRemovedMetricsForSubtitle(
  result:
    | Pick<TradePolicySimulateResponse, "meta" | "removed" | "changed" | "baseline" | "filtered">
    | null
    | undefined
): TradePolicyMetric | undefined {
  return result?.removed ?? deriveRemovedMetrics(result?.baseline, result?.filtered);
}

/**
 * Absolute filtered−baseline delta for the Final book vs Baseline column (paired with % change).
 * Win rate / max DD % use percentage-point deltas.
 */
export function formatMetricAbsoluteDelta(key: string, baselineVal: unknown, filteredVal: unknown): string | null {
  if (key === "win_rate") {
    const b = normalizeWinRatePercent(baselineVal);
    const f = normalizeWinRatePercent(filteredVal);
    if (b == null || f == null) return null;
    return `${formatSignedNumber(f - b)}%`;
  }
  if (key === "max_drawdown_percent") {
    if (typeof baselineVal !== "number" || typeof filteredVal !== "number") return null;
    return `${formatSignedNumber(filteredVal - baselineVal)}%`;
  }
  const delta = metricDeltaNumber(baselineVal, filteredVal);
  if (delta == null) return null;
  if (
    key === "trade_count" ||
    key === "win_count" ||
    key === "loss_count"
  ) {
    return formatSignedNumber(delta, 0);
  }
  if (key === "total_pnl" || key === "expectancy" || key === "max_drawdown") {
    return formatSignedNumber(delta, 4);
  }
  return formatSignedNumber(delta);
}

/** @deprecated Prefer {@link resolveComparisonLaneColumns}; returns the Changed column when present. */
export function resolveComparisonLaneColumn(
  result:
    | Pick<TradePolicySimulateResponse, "meta" | "removed" | "changed" | "baseline" | "filtered">
    | null
    | undefined
): ComparisonLaneColumn {
  return (
    resolveComparisonLaneColumns(result)[0] ?? {
      lane: "changed",
      header: "Changed",
      metrics: undefined,
    }
  );
}
